import { useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0DatePicker, F0Dialog } from '@factorialco/f0-react'
import { DatePickerValue, Input } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment-timezone'

import i18n from 'modules/core/lib/i18n'

interface SchedulePostModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (scheduledDate: string) => void
  initialDate?: Date
  initialTime?: string
}

function SchedulePostModal({
  isOpen,
  onClose,
  onConfirm,
  initialDate,
  initialTime,
}: SchedulePostModalProps) {
  const userTimezone = moment.tz.guess()
  const timezoneAbbr = useMemo(() => {
    return moment.tz(userTimezone).format('z')
  }, [userTimezone])

  const defaultDate = initialDate || new Date()
  const defaultTime = initialTime || moment().format('HH:mm')

  const [selectedDate, setSelectedDate] = useState<Date>(defaultDate)
  const [selectedTime, setSelectedTime] = useState<string>(defaultTime)

  const handleDateChange = (
    date: DatePickerValue | undefined,
    _stringValue?: string | undefined
  ) => {
    if (date?.value?.from) {
      setSelectedDate(date.value.from)
    }
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
  }

  const handleConfirm = () => {
    const [hours, minutes] = selectedTime.split(':')
    if (!hours || !minutes) return

    const scheduledMoment = moment(selectedDate)
      .set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10), seconds: 0 })
      .tz(userTimezone)

    onConfirm(scheduledMoment.toISOString())
    onClose()
  }

  const datePickerValue = useMemo(
    () => ({
      granularity: 'day' as const,
      value: {
        from: selectedDate,
        to: selectedDate,
      },
    }),
    [selectedDate]
  )

  return (
    <F0Dialog
      isOpen={isOpen}
      width="sm"
      onClose={onClose}
      title={i18n.t('posts.communities.schedule_post.title')}
      primaryAction={{
        label: i18n.t('posts.communities.schedule_post.confirm'),
        onClick: handleConfirm,
      }}
      secondaryAction={{
        label: i18n.t('posts.communities.schedule_post.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16" flexDirection="row" width="full">
        <Box style={{ flex: '1' }}>
          <F0DatePicker
            label={i18n.t('posts.communities.schedule_post.date')}
            onChange={handleDateChange}
            size="sm"
            value={datePickerValue}
            minDate={new Date()}
          />
        </Box>

        <Box flexDirection="row" alignItems="flexEnd" gap="s8" style={{ flex: '1' }}>
          <Box style={{ flex: '1' }}>
            <Input
              label={i18n.t('posts.communities.schedule_post.time')}
              type="time"
              value={selectedTime}
              onChange={handleTimeChange}
            />
          </Box>
          <Box paddingBottom="s8" style={{ minWidth: '50px' }}>
            <Text.Normal size="200" color="grey600" weight="medium">
              {timezoneAbbr}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default SchedulePostModal
